import { Controller } from "@hotwired/stimulus"

const ICON_X = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
const ICON_CHEVRON_LEFT = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>'
const ICON_CHEVRON_RIGHT = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>'

export default class extends Controller {
  connect() {
    this.items = []
    this.currentIndex = 0
    this.boundKeydown = this._handleKeydown.bind(this)

    this.element.querySelectorAll("[data-gallery-lightbox-item]").forEach((el) => {
      this.items.push({
        src: el.dataset.galleryLightboxSrc,
        caption: el.dataset.galleryLightboxCaption || "",
        element: el
      })
    })

    if (this.items.length === 0) return

    this._buildOverlay()
    this._bindTouchEvents()
  }

  disconnect() {
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this.boundKeydown)
    if (this.overlayEl && this.overlayEl.parentNode) {
      this.overlayEl.parentNode.removeChild(this.overlayEl)
    }
  }

  open(event) {
    event.preventDefault()
    const clickedEl = event.currentTarget
    const index = this.items.findIndex((item) => item.element === clickedEl)
    if (index === -1) return

    this.currentIndex = index
    this._updateDisplay()
    this.overlayEl.style.display = "flex"
    requestAnimationFrame(() => {
      this.overlayEl.style.opacity = "1"
    })
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this.boundKeydown)
  }

  close() {
    if (this._closeTimer) clearTimeout(this._closeTimer)
    this.overlayEl.style.opacity = "0"
    this._closeTimer = setTimeout(() => {
      this.overlayEl.style.display = "none"
      this._closeTimer = null
    }, 200)
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this.boundKeydown)
  }

  prev() {
    const count = this.items.length
    this.currentIndex = (this.currentIndex - 1 + count) % count
    this._updateDisplay()
  }

  next() {
    const count = this.items.length
    this.currentIndex = (this.currentIndex + 1) % count
    this._updateDisplay()
  }

  _handleKeydown(event) {
    if (event.key === "Escape") this.close()
    else if (event.key === "ArrowLeft") this.prev()
    else if (event.key === "ArrowRight") this.next()
  }

  _closeOnBackdrop(event) {
    if (event.target === this.overlayEl) {
      this.close()
    }
  }

  _updateDisplay() {
    const item = this.items[this.currentIndex]
    this.imageEl.src = item.src
    this.imageEl.alt = item.caption
    this.counterEl.textContent = `${this.currentIndex + 1} / ${this.items.length}`
    this.captionEl.textContent = item.caption
    this.captionEl.style.display = item.caption ? "block" : "none"
    this._preloadAdjacent()
  }

  _preloadAdjacent() {
    const count = this.items.length
    if (count <= 1) return
    const prevIdx = (this.currentIndex - 1 + count) % count
    const nextIdx = (this.currentIndex + 1) % count
    new Image().src = this.items[prevIdx].src
    new Image().src = this.items[nextIdx].src
  }

  _buildOverlay() {
    const overlay = document.createElement("div")
    overlay.style.cssText = "position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.9);display:none;align-items:center;justify-content:center;opacity:0;transition:opacity 200ms ease;"
    overlay.addEventListener("click", (e) => this._closeOnBackdrop(e))

    // Counter
    const counter = document.createElement("div")
    counter.style.cssText = "position:absolute;top:1.25rem;left:1.5rem;color:#fff;font-size:14px;font-weight:600;user-select:none;"
    this.counterEl = counter

    // Close button
    const closeBtn = this._createNavButton(ICON_X)
    closeBtn.style.cssText += "position:absolute;top:1rem;right:1rem;"
    closeBtn.addEventListener("click", (e) => { e.stopPropagation(); this.close() })

    // Image
    const img = document.createElement("img")
    img.style.cssText = "max-width:90vw;max-height:80vh;object-fit:contain;display:block;user-select:none;"
    img.draggable = false
    this.imageEl = img

    // Caption
    const caption = document.createElement("div")
    caption.style.cssText = "position:absolute;bottom:2rem;left:50%;transform:translateX(-50%);color:#fff;max-width:80%;text-align:center;font-size:14px;line-height:1.6;user-select:none;"
    this.captionEl = caption

    // Prev button
    const prevBtn = this._createNavButton(ICON_CHEVRON_LEFT)
    prevBtn.style.cssText += "position:absolute;left:1rem;top:50%;transform:translateY(-50%);"
    prevBtn.addEventListener("click", (e) => { e.stopPropagation(); this.prev() })

    // Next button
    const nextBtn = this._createNavButton(ICON_CHEVRON_RIGHT)
    nextBtn.style.cssText += "position:absolute;right:1rem;top:50%;transform:translateY(-50%);"
    nextBtn.addEventListener("click", (e) => { e.stopPropagation(); this.next() })

    // Hide nav if only 1 image
    if (this.items.length <= 1) {
      prevBtn.style.display = "none"
      nextBtn.style.display = "none"
      counter.style.display = "none"
    }

    overlay.appendChild(counter)
    overlay.appendChild(closeBtn)
    overlay.appendChild(prevBtn)
    overlay.appendChild(img)
    overlay.appendChild(nextBtn)
    overlay.appendChild(caption)

    document.body.appendChild(overlay)
    this.overlayEl = overlay
  }

  _createNavButton(iconSvg) {
    const btn = document.createElement("button")
    const size = window.matchMedia("(max-width: 768px)").matches ? "36px" : "48px"
    btn.style.cssText = `width:${size};height:${size};background:rgba(0,0,0,0.5);border:none;color:#fff;cursor:pointer;padding:0.5rem;display:flex;align-items:center;justify-content:center;`
    btn.innerHTML = iconSvg
    btn.addEventListener("mouseenter", () => { btn.style.background = "rgba(255,255,255,0.2)" })
    btn.addEventListener("mouseleave", () => { btn.style.background = "rgba(0,0,0,0.5)" })
    return btn
  }

  _bindTouchEvents() {
    if (!this.overlayEl) return
    this.overlayEl.addEventListener("touchstart", (e) => {
      this._touchStartX = e.changedTouches[0].clientX
      this._touchStartY = e.changedTouches[0].clientY
    }, { passive: true })
    this.overlayEl.addEventListener("touchend", (e) => {
      if (this._touchStartX === undefined) return
      const deltaX = e.changedTouches[0].clientX - this._touchStartX
      const deltaY = e.changedTouches[0].clientY - this._touchStartY
      this._touchStartX = undefined
      this._touchStartY = undefined
      if (Math.abs(deltaY) > Math.abs(deltaX)) return
      if (Math.abs(deltaX) < 50) return
      if (deltaX < 0) this.next()
      else this.prev()
    }, { passive: true })
  }
}
